import { useContext, useMemo, useState } from "react";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import styles from "./mortgage-view.module.scss";
import FileUploadInput from "../../components/Input/file-upload-input";
import { useQuery } from "../../hooks/useQuery";
import { useHistory } from "react-router";
import ToastContext from "../../contexts/ToastContext";
import LoadingContext from "../../contexts/LoadingContext";
import {
  mortgageDocumentUploadApi,
  submitMortgageApplication,
} from "../../swr/mortgage";
import { DocumentTypes } from "../../types/mortgage";
import ModalLayout from "../../components/ModalLayout";
import SuccessModal from "../../components/Modal/success-modal";

const fileSizeTest = (file: File | undefined) =>
  file ? file.size / (1024 * 1024) <= 10 : false;

const requiredFile = (msg: string) =>
  yup
    .mixed<File>()
    .required(msg)
    .test("fileSize", "File must be 10MB or less", fileSizeTest);

const optionalFile = () =>
  yup.mixed<File>().notRequired().default(undefined) as yup.MixedSchema<File | undefined>;

const buildSchema = (isBusinessApplicant: boolean) =>
  yup.object({
    // Common to all
    propertyPhoto: requiredFile("Property photo is required"),
    statementOfAccount: requiredFile("Statement Of Bank Account is required"),
    titledDocument: requiredFile("Titled Document of Property is required"),
    aceptanceLetter: requiredFile("Acceptance letter of Property Owner is required"),
    meansOfIdentification: requiredFile("Means of Identification is required"),

    // Salary only
    letterOfEmployment: !isBusinessApplicant
      ? requiredFile("Letter of Employment is required")
      : optionalFile(),
    paySlip: !isBusinessApplicant
      ? requiredFile("Last 6 months Payslip is required")
      : optionalFile(),
    evidenceOfPayment: !isBusinessApplicant
      ? requiredFile("Evidence of Payment is required")
      : optionalFile(),

    // Business only
    businessRegistration: isBusinessApplicant
      ? requiredFile("Business Registration Document is required")
      : optionalFile(),
  });

const DocumentUploadExtMort = () => {
  let query = useQuery();
  const isBusinessApplicant = query.get("epytppa") === "business";
  const schema = useMemo(() => buildSchema(isBusinessApplicant), [isBusinessApplicant]);
 
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Record<string, File>>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      propertyPhoto: undefined,
      statementOfAccount: undefined,
      titledDocument: undefined,
      aceptanceLetter: undefined,
      meansOfIdentification: undefined,
      letterOfEmployment: undefined,
      paySlip: undefined,
      evidenceOfPayment: undefined,
      businessRegistration: undefined,
    },
  });
  const history = useHistory();

  const { openError } = useContext(ToastContext);
  const { setLoading } = useContext(LoadingContext);

  const [submitSuccessful, setSubmitSuccessful] = useState(false);

  const clickOutSuccessModal = () => {
    setSubmitSuccessful(false);
    history.push("/mortgage-categories");
  };

  const onSubmit = async (req: Record<string, File>) => {
    setLoading(true);
    try {
      const appId = query.get("dippa");

      const entries = Object.entries(req).filter(([, file]) => file != null);
      await mortgageDocumentUploadApi({
        MortgageApplicationId: appId ? Number(appId) : 0,
        DocumentTypes: entries.map(
          ([doc]) =>
            DocumentTypes.find((type) => type.finder === doc)?.value ?? 0
        ),
        Files: entries.map(([, file]) => file),
      });

      const { data, error } = await submitMortgageApplication(
        appId ? Number(appId) : 0
      );

      if (data) {
        setSubmitSuccessful(true);
      }

      if (error) {
        openError(
          error?.response?.data?.Message ||
            error?.response?.data?.message ||
            "An error occurred"
        );
      }
    } catch (error: any) {
      openError(
        error?.response?.data?.Message ||
          error?.response?.data?.message ||
          "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.container}>
          <span className={styles.instruction}>
            Please fill the form below to begin your mortgage application
            process
          </span>

          <div className={styles.grid}>
            {/* Common to all */}
            <Controller
              name="propertyPhoto"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Property Photo"
                  placeholder="Upload property photo"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.propertyPhoto}
                />
              )}
            />
            <Controller
              name="meansOfIdentification"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Means of Identification (NIN, Voter’s Card or International Passport)"
                  placeholder="Upload Means of Identification"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.meansOfIdentification}
                />
              )}
            />
            <Controller
              name="statementOfAccount"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Statement of Bank Account for the Last 12 Months"
                  placeholder="Upload Bank Account Statement"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.statementOfAccount}
                />
              )}
            />
            <Controller
              name="titledDocument"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Titled Document of Property"
                  placeholder="Upload titled document"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.titledDocument}
                />
              )}
            />
            <Controller
              name="aceptanceLetter"
              control={control}
              render={({ field }) => (
                <FileUploadInput
                  header="Offer & Acceptance Letter of Property Owner"
                  placeholder="Upload Acceptance Letter of Property Owner"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.aceptanceLetter}
                />
              )}
            />

            {/* Salary only */}
            {!isBusinessApplicant && (
              <>
                <Controller
                  name="letterOfEmployment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Letter of Employment"
                      placeholder="Upload letter of employment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.letterOfEmployment}
                    />
                  )}
                />
                <Controller
                  name="paySlip"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Last 6 months Payslip"
                      placeholder="Upload 6 months payslip"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.paySlip}
                    />
                  )}
                />
                <Controller
                  name="evidenceOfPayment"
                  control={control}
                  render={({ field }) => (
                    <FileUploadInput
                      header="Evidence of Additional Payment for the Past 12 Months"
                      placeholder="Upload evidence of payment"
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.evidenceOfPayment}
                    />
                  )}
                />
              </>
            )}

            {/* Business only */}
            {isBusinessApplicant && (
              <Controller
                name="businessRegistration"
                control={control}
                render={({ field }) => (
                  <FileUploadInput
                    header="Business Registration Document"
                    placeholder="Upload business registration document"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.businessRegistration}
                  />
                )}
              />
            )}
          </div>

          <div className={styles["bottom-cont"]}>
            <div
              className={styles["back-but"]}
              role="button"
              onClick={() => history.goBack()}
            >
              <span>Back</span>
            </div>
            <button type="submit" className={styles["submit-btn"]}>
              Submit Application
            </button>
          </div>
        </div>
      </form>

      <ModalLayout
        modalOpen={submitSuccessful}
        onClickOut={() => clickOutSuccessModal()}
      >
        <SuccessModal
          onClose={() => clickOutSuccessModal()}
          message="Your application has been submitted. We'll be in touch shortly."
        />
      </ModalLayout>
    </>
  );
};

export default DocumentUploadExtMort;
